import {
  Pagination,
  PermissionDetail,
  RoleViewFilters,
} from '@agilelab/plugin-wb-rbac-common';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';

export interface RoleDrawerContextType {
  filters: RoleViewFilters;
  changeFilters: <K extends keyof RoleViewFilters>(
    key: K,
    value: RoleViewFilters[K],
  ) => void;
  pagination: Pagination;
  setPagination: (p: Pagination) => void;
  permissions: PermissionDetail[];
  permissionCount: number;
  reloadTable: () => void;
}

export const RoleDrawerContext = createContext<RoleDrawerContextType>(
  {} as RoleDrawerContextType,
);

export const RoleDrawerContextProvider: FC<{
  children: any;
  subject: string[];
  target: string;
  permission: string;
}> = ({ children, permission, target }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [permissions, setPermissions] = useState<PermissionDetail[]>([]);

  const [filters, setFilters] = useState<RoleViewFilters>({
    text: '',
    limit: 25,
  });
  const [pagination, setPagination] = useState<Pagination>({
    limit: filters.limit ?? 25,
    offset: 0,
  });

  useAsync(async () => {
    const response = await fetchApi.fetch(
      `${await discoveryApi.getBaseUrl(
        'rbac',
      )}/permissiondetails?permission=${permission}&target=${target}`,
    );

    const data = await response.json();

    setPermissions(data.permissionDetails);
  });

  const filteredPermissions = useMemo(() => {
    return permissions.filter(tempPermission => {
      return (
        (tempPermission.subjectdisplayname ?? '')
          .toLowerCase()
          .includes((filters.text ?? '').toLowerCase()) ||
        (tempPermission.display_name ?? '')
          .toLowerCase()
          .includes((filters.text ?? '').toLowerCase()) ||
        (tempPermission.description ?? '')
          .toLowerCase()
          .includes((filters.text ?? '').toLowerCase())
      );
    });
  }, [filters.text, permissions]);

  const paginatedPermisions = useMemo(() => {
    return filteredPermissions.slice(
      pagination.offset,
      pagination.offset + pagination.limit,
    );
  }, [filteredPermissions, pagination]);

  const reloadTable = useCallback(() => {
    setPagination({ ...pagination, offset: 0 });
  }, [setPagination, pagination]);

  const contextValue = useMemo(
    () => ({
      filters,
      changeFilters: <K extends keyof RoleViewFilters>(
        key: K,
        filterValue: RoleViewFilters[K],
      ) => {
        setPagination(p => ({ ...p, offset: 0 }));
        setFilters(f => ({ ...f, [key]: filterValue }));
      },
      pagination,
      setPagination,
      permissions: paginatedPermisions,
      permissionCount: filteredPermissions.length,
      reloadTable,
    }),
    [
      filters,
      pagination,
      paginatedPermisions,
      filteredPermissions.length,
      reloadTable,
    ],
  );

  return (
    <RoleDrawerContext.Provider value={contextValue}>
      {children}
    </RoleDrawerContext.Provider>
  );
};

export const useRoleDrawerContext = () => useContext(RoleDrawerContext);
